import {
  allParentChildrenTotalsAreValid,
  allRequiredFieldsAreValid,
  allRequiredFieldsHaveContent,
  areAmountValuesEquivalent,
  buildDraftOverheads,
  buildParentChildrenTotalErrors,
  isEmptyDraftValue,
} from "./overheadsAutosave.model";

export const selectDraftOverheads = buildDraftOverheads;
export const selectParentChildrenTotalErrors = buildParentChildrenTotalErrors;
export const selectHasParentChildrenTotalErrors = (state) => Object.keys(selectParentChildrenTotalErrors(state)).length > 0;
export const selectHasFieldErrors = (state) => state.fieldOrder.some((fieldKey) => state.fields[fieldKey]?.phase === "error");
export const selectHasAnyValidationErrors = (state) => selectHasFieldErrors(state) || selectHasParentChildrenTotalErrors(state);

export const selectFilledOutProgress = (state) => {
  const requiredFields = state.fieldOrder.map((fieldKey) => state.fields[fieldKey]).filter((field) => field?.required);
  const total = requiredFields.length;
  const completed = requiredFields.filter((field) => !isEmptyDraftValue(field.draftValue)).length;
  return {
    completed,
    total,
    remaining: Math.max(total - completed, 0),
    percentage: total ? (completed / total) * 100 : 0,
    isComplete: total > 0 && completed === total,
  };
};

export const selectHasQueuedFields = (state) =>
  Object.keys(state.persistence.pendingByField).length > 0 || state.fieldOrder.some((fieldKey) => state.fields[fieldKey]?.phase === "queued");

export const selectHasSavingFields = (state) =>
  state.persistence.phase === "saving" || state.fieldOrder.some((fieldKey) => state.fields[fieldKey]?.phase === "saving");

export const selectHasUnconfirmedChanges = (state) => {
  if (
    state.persistence.phase === "saving" ||
    state.persistence.phase === "reconciling" ||
    state.persistence.phase === "unverified" ||
    Object.keys(state.persistence.pendingByField).length > 0
  ) {
    return true;
  }

  return state.fieldOrder.some((fieldKey) => {
    const field = state.fields[fieldKey];
    if (!field) return false;
    if (field.phase === "editing" || field.phase === "queued" || field.phase === "saving") return true;
    return !areAmountValuesEquivalent(field.draftValue, field.confirmedValue);
  });
};

export const selectAllRelevantFieldsConfirmed = (state) =>
  state.fieldOrder.every((fieldKey) => {
    const field = state.fields[fieldKey];
    if (!field) return false;
    return (
      (field.phase === "saved" || (!field.required && field.phase === "pristine")) &&
      field.error == null &&
      areAmountValuesEquivalent(field.draftValue, field.confirmedValue)
    );
  });

export const selectIsOverheadsComplete = (state) =>
  state.fieldOrder.length > 0 &&
  state.persistence.phase === "idle" &&
  Object.keys(state.persistence.pendingByField).length === 0 &&
  !selectHasAnyValidationErrors(state) &&
  !selectHasUnconfirmedChanges(state) &&
  allRequiredFieldsHaveContent(state) &&
  allRequiredFieldsAreValid(state) &&
  allParentChildrenTotalsAreValid(state) &&
  selectAllRelevantFieldsConfirmed(state);

const BADGE_TRANSLATION_KEYS = {
  saving: "benchmarking.survey.company-overhead.autosave-status.saving",
  success: "benchmarking.survey.company-overhead.autosave-status.changes-saved",
  error: "benchmarking.survey.company-overhead.autosave-status.invalid-fields",
  saved: "benchmarking.survey.company-overhead.autosave-status.saved",
};

export const selectBadge = (state, now = Date.now()) => {
  const hasActiveSave = selectHasQueuedFields(state) || selectHasSavingFields(state);
  if (hasActiveSave) return { kind: "saving", labelKey: BADGE_TRANSLATION_KEYS.saving };
  const hasRecentSuccessfulSave = state.lastSuccessfulSaveAt != null && now - state.lastSuccessfulSaveAt < 1500;
  if (hasRecentSuccessfulSave) return { kind: "success", labelKey: BADGE_TRANSLATION_KEYS.success };
  const hasErrors = selectHasAnyValidationErrors(state) || state.persistence.phase === "reconciling" || state.persistence.phase === "unverified";
  if (hasErrors) return { kind: "error", labelKey: BADGE_TRANSLATION_KEYS.error };
  return { kind: "saved", labelKey: BADGE_TRANSLATION_KEYS.saved };
};
